import { createAction } from '@reduxjs/toolkit';
import type { DsBreakageNotification } from './reducers';
import type { DesignSystemContent } from 'designSystem/content/types';
import { ComponentCategory, DesignSystemSection, DsViewPreferences, TokenCategory } from 'designSystem/types';
import { Point, Rect } from 'dragonmouse-shared/geometry';
import { AuthUser, DomElement, DsPick, DsPlan } from 'dragonmouse-shared/interfaces';
import { DeviceConfig, DsTab, Settings } from 'redux/mode/reducers';
import { TransferBoard } from 'transfer/TransferBoard';
import { Pick } from './classes';
import { Command } from './commands';
import {
	AlertModalOptions,
	ConfirmModalOptions,
	ContextMenu,
	DarkModeBehavior,
	LeftPanelTab,
	MouseDragMode,
	PendingChatInput,
	PendingDsChatInput,
	PromptModalOptions,
	SceneTransform,
	ToolType,
	ZoomType,
} from './reducers';

export const setActiveTool = createAction<ToolType>('SET_ACTIVE_TOOL');
export const setToolModified = createAction<boolean>('SET_TOOL_MODIFIED');
export const activateLastTool = createAction('ACTIVATE_LAST_TOOL');
export const setScale = createAction<number>('SET_SCALE');
export const setDarkModeBehavior = createAction<DarkModeBehavior>('SET_DARK_MODE_BEHAVIOR');
export const setSystemDarkMode = createAction<boolean>('SET_SYSTEM_DARK_MODE');
export const setOffset = createAction<Point>('SET_OFFSET');
export const resetOffset = createAction('RESET_OFFSET');
export const centerCanvasAroundScenePosition = createAction<Point>(
	'CENTER_CANVAS_AROUND_SCENE_POSITION'
);
// transforms the canvas as little as possible to show the provided bounding box
// if the bounding box is already fully visible, this will do nothing
export const transformCanvasToShowSceneBoundingBox = createAction<Rect>(
	'TRANSFORM_CANVAS_TO_SHOW_BOUNDING_BOX'
);
// transforms the canvas to neatly center the provided bounding box in view
export const centerCanvasAroundSceneBoundingBox = createAction<{
	boundingBox: Rect;
	animated: boolean;
	tightFit?: boolean;
	frameId?: string; // if frameId is provided, the specified frame will be focused
}>('CENTER_CANVAS_AROUND_BOUNDING_BOX');
export const setCanvasMousePosition = createAction<Point>('SET_CANVAS_MOUSE_POSITION');
export const setCanvasWidth = createAction<number>('SET_CANVAS_WIDTH');
export const setCanvasHeight = createAction<number>('SET_CANVAS_HEIGHT');
export const zoomSetAroundCanvasCenter = createAction<number>('ZOOM_SET_AROUND_CANVAS_CENTER');
// value is a float, representing the amount to multiple the scale factor by
// value = 2.0 means we will zoom in by 2x
export const zoomAroundCanvasCenter = createAction<number>('ZOOM_AROUND_CANVAS_CENTER');
export const zoomAroundMousePosition = createAction<ZoomType>('ZOOM_AROUND_MOUSE_POSITION');
export const setScaleAroundMousePosition = createAction<number>('SET_SCALE_AROUND_MOUSE_POSITION');
export const setMouseDragMode = createAction<MouseDragMode | null>('SET_MOUSE_DRAG_MODE');
export const setLoading = createAction<boolean>('SET_LOADING');
export const setExternalLocation = createAction<boolean>('SET_EXTERNAL_LOCATION');
export const setPanelsVisible = createAction<boolean>('SET_PANELS_VISIBLE');
export const setSidebarVisible = createAction<boolean>('SET_SIDEBAR_VISIBLE');
export const setSidebarDocked = createAction<boolean>('SET_SIDEBAR_DOCKED');
export const setSidebarHovered = createAction<boolean>('SET_SIDEBAR_HOVERED');
export const setLeftPanelWidth = createAction<{ tab: LeftPanelTab; width: number }>(
	'SET_LEFT_PANEL_WIDTH'
);
export const setRightPanelWidth = createAction<number>('SET_RIGHT_PANEL_WIDTH');
export const setInspectorRightPanelWidth = createAction<number>('SET_INSPECTOR_RIGHT_PANEL_WIDTH');
export const setInspectorLeftPanelWidth = createAction<number>('SET_INSPECTOR_LEFT_PANEL_WIDTH');
export const setDesignSystemChatPanelWidth = createAction<number>(
	'SET_DESIGN_SYSTEM_CHAT_PANEL_WIDTH'
);
export const setInspectorDeepThinking = createAction<boolean>('SET_INSPECTOR_DEEP_THINKING');
export const setActiveDsMode = createAction<string | null>('SET_ACTIVE_DS_MODE');
export const setSelectedDomElements = createAction<DomElement[]>('SET_SELECTED_DOM_ELEMENTS');
export const showContextMenu = createAction<ContextMenu | null>('SHOW_CONTEXT_MENU');
export const showPromptModal = createAction<PromptModalOptions | null>('SHOW_PROMPT_MODAL');
export const showConfirmModal = createAction<ConfirmModalOptions | null>('SHOW_CONFIRM_MODAL');
export const showAlertModal = createAction<AlertModalOptions | null>('SHOW_ALERT_MODAL');
export const showMigrationModal = createAction<{
	dsId: string;
	context: 'editor' | 'scene';
	sceneId?: string;
} | null>('SHOW_MIGRATION_MODAL');
export const setPlanPricingModalVisible = createAction<boolean>('SET_PLAN_PRICING_MODAL_VISIBLE');
export const setPlanPricingModalPending = createAction<boolean>('SET_PLAN_PRICING_MODAL_PENDING');
// hovering and selection
// pass null into these to clear selection/hover
export const setSelected = createAction<Pick | Pick[] | null>('SET_SELECTED');
export const addToSelected = createAction<Pick>('ADD_TO_SELECTED');
export const removeFromSelected = createAction<Pick>('REMOVE_FROM_SELECTED');
export const setHovered = createAction<Pick | null>('SET_HOVERED');
export const setClipboard = createAction<TransferBoard | null>('SET_CLIPBOARD');
export const setUpdateNavigatorScrollPosition = createAction<boolean>(
	'SET_UPDATE_NAVIGATOR_SCROLL_POSITION'
);
export const setSettingsModalVisible = createAction<boolean>('SET_SETTINGS_MODAL_VISIBLE');
export const setActiveSceneId = createAction<string | null>('SET_ACTIVE_SCENE_ID');
export const setActiveDesignSystemId = createAction<string | null>('SET_ACTIVE_DESIGN_SYSTEM_ID');
export const setActiveSceneChatId = createAction<string | null>('SET_ACTIVE_SCENE_CHAT_ID');
export const setActiveDsChatId = createAction<string | null>('SET_ACTIVE_DS_CHAT_ID');
export const setPendingChatInput = createAction<{
	chatId: string;
	pendingChatInput: PendingChatInput;
}>('SET_PENDING_CHAT_INPUT');
export const setPendingDsChatInput = createAction<{
	dsChatId: string;
	pendingDsChatInput: PendingDsChatInput;
}>('SET_PENDING_DS_CHAT_INPUT');
export const setChatReferenceText = createAction<string | null>('SET_CHAT_REFERENCE_TEXT');
export const setAuthUser = createAction<AuthUser>('SET_AUTH_USER');
export const setUserReady = createAction<boolean>('SET_USER_READY');
export const setToolModalMousePosition = createAction<Point | null>(
	'SET_TOOL_MODAL_MOUSE_POSITION'
);
export const setFigmaIntegrationEnabled = createAction<boolean>('SET_FIGMA_INTEGRATION_ENABLED');
export const updateTransformDuringAnimation = createAction<{
	sceneId: string;
	transform: SceneTransform;
}>('UPDATE_TRANSFORM_DURING_ANIMATION');
export const cancelTransformAnimation = createAction('CANCEL_TRANSFORM_ANIMATION');
export const setVisibleToolModal = createAction<ToolType | null>('SET_VISIBLE_TOOL_MODAL');
export const setZoomPreviewBox = createAction<Rect | null>('SET_ZOOM_PREVIEW_BOX');
export const setSettings = createAction<Partial<Settings>>('SET_SETTINGS');
export const setDefaultOrganizationId = createAction<string | null>('SET_DEFAULT_ORGANIZATION_ID');
export const setChatDesignSystemMode = createAction<string | null>('SET_CHAT_DESIGN_SYSTEM_MODE');
export const setMergeInstruction = createAction<{ frameId: string; instruction: string }>(
	'SET_MERGE_INSTRUCTION'
);
export const setActiveLeftPanelTab = createAction<LeftPanelTab>('SET_ACTIVE_LEFT_PANEL_TAB');

export const setCenterFramesAsChatGeneratesThem = createAction<boolean>(
	'mode/setCenterFramesAsChatGeneratesThem'
);
export const setPrototypeScaling = createAction<'mockup' | 'actual-size'>('SET_PROTOTYPE_SCALING');
export const doCommand = createAction<Command>('DO_COMMAND');
export const undoCommand = createAction('UNDO_COMMAND');
export const redoCommand = createAction('REDO_COMMAND');
export const setFrameCentered = createAction<string | null>('SET_FRAME_CENTERED');
export const setCenteredFrameId = createAction<string | null>('SET_CENTERED_FRAME_ID');
export const setIsDragging = createAction<boolean>('SET_IS_DRAGGING');
export const setActiveCommentId = createAction<string | null>('SET_ACTIVE_COMMENT_ID');
export const setHoveredCommentId = createAction<string | null>('SET_HOVERED_COMMENT_ID');
export const setCommentReorderPanelExpanded = createAction<boolean>(
	'SET_COMMENT_REORDER_PANEL_EXPANDED'
);
export const addCommentIntroShownForScene = createAction<string>(
	'ADD_COMMENT_INTRO_SHOWN_FOR_SCENE'
);

export const addTrackedUserId = createAction<string>('ADD_TRACKED_USER_ID');
export const removeTrackedUserId = createAction<string>('REMOVE_TRACKED_USER_ID');

export const setDeviceConfig = createAction<DeviceConfig>('SET_DEVICE_CONFIG');

export const setDesignSystemContent = createAction<DesignSystemContent | null>(
	'SET_DESIGN_SYSTEM_CONTENT'
);

export const toggleDesignSystemFilterCategory = createAction<{
	section: 'theme' | 'components';
	category: TokenCategory | ComponentCategory;
}>('TOGGLE_DESIGN_SYSTEM_FILTER_CATEGORY');

export const setDesignSystemFilterCategories = createAction<{
	section: 'theme' | 'components';
	categories: (TokenCategory | ComponentCategory)[];
}>('SET_DESIGN_SYSTEM_FILTER_CATEGORIES');

export const clearDesignSystemFilters = createAction<DesignSystemSection>(
	'CLEAR_DESIGN_SYSTEM_FILTERS'
);

export const setPendingDsPlan = createAction<DsPlan | null>('SET_PENDING_DS_PLAN');

export const addDsTab = createAction<DsTab>('ADD_DS_TAB');
export const removeDsTab = createAction<string>('REMOVE_DS_TAB');
export const setActiveDsTab = createAction<string | null>('SET_ACTIVE_DS_TAB');
export const setDsTabs = createAction<DsTab[]>('SET_DS_TABS');
export const updateDsTab = createAction<{ id: string; contentPath?: string; label?: string }>('UPDATE_DS_TAB');
export const setDsTabScrollPosition = createAction<{ tabId: string; scrollTop: number }>(
	'SET_DS_TAB_SCROLL_POSITION'
);

export const setDsViewPreference = createAction<Partial<DsViewPreferences>>('SET_DS_VIEW_PREFERENCE');

export const toggleDsPick = createAction<DsPick>('TOGGLE_DS_PICK');
export const removeDsPick = createAction<DsPick>('REMOVE_DS_PICK');
export const clearDsPicks = createAction('CLEAR_DS_PICKS');

export const addDsBreakageNotification = createAction<DsBreakageNotification>(
	'ADD_DS_BREAKAGE_NOTIFICATION'
);
export const dismissDsBreakageNotification = createAction<string>(
	'DISMISS_DS_BREAKAGE_NOTIFICATION'
);
export const clearDsBreakageNotifications = createAction('CLEAR_DS_BREAKAGE_NOTIFICATIONS');
// removes notifications whose removedSymbols are all covered by the resolved set
export const resolveBreakageSymbols = createAction<string[]>('RESOLVE_BREAKAGE_SYMBOLS');

export type DsFileOptimisticWrite = {
	id: string;
	path: string;
	content: string;
	createdAt: number;
};
export const optimisticallyUpdateDsFiles = createAction<DsFileOptimisticWrite[]>(
	'OPTIMISTICALLY_UPDATE_DS_FILES'
);
export const resolveOptimisticDsFileWrites = createAction<string[]>(
	'RESOLVE_OPTIMISTIC_DS_FILE_WRITES'
);

export const setNewVersionAvailable = createAction<boolean>('SET_NEW_VERSION_AVAILABLE');
